import React from 'react';
import styled, { css } from 'styled-components';
import { IconAnimatedChevron } from '../../icon';
import { Flex, Aside } from '../../box';
import Text from '../../text';
import Collapse from '../../collapse';
import { colors, spacing } from '../../../style';

interface AsideContainerProps {
  children?: React.ReactNode;
  maxWidth?: string;
  minWidth?: string;
  hideScrollbar?: boolean;
  mode?: keyof typeof modes;
  variant?: keyof typeof variants;
  style?: React.CSSProperties;
  className?: string;
  innerContentPosition?: 'relative' | 'fixed';
}

export const AsideContainerComponent = ({
  children,
  maxWidth = '300px',
  minWidth = '300px',
  ...rest
}: AsideContainerProps) => {
  return (
    <Aside
      flexShrink={0}
      height={'100%'}
      flexGrow={1}
      maxWidth={{ xs: '100%', sm: maxWidth }}
      minWidth={minWidth}
      {...rest}
    >
      {children}
    </Aside>
  );
};

const variants = {
  'dark-blue': css`
    background-color: ${colors.darkBlue};
  `,
  'gray300-border': css`
    border-right: 1px solid ${colors.gray300};
    background-color: ${colors.white};
  `,
  'gray300-border-left': css`
    border-left: 1px solid ${colors.gray300};
    background-color: ${colors.white};
  `,
  shadow: css`
    box-shadow: 3px 3px 6px -3px rgba(0, 0, 0, 0.2);
  `,
  'shadow-white': css`
    background: white;
    box-shadow: 3px 3px 6px -3px rgba(0, 0, 0, 0.2);
  `,
  'pink-light': css`
    background: #fff0f0;
    border-right: 1px solid #f4d5d5;
  `,

  default: '',
};

const modes = {
  default: {
    'padding-left': spacing['md'],
    'padding-right': spacing['md'],
    'padding-top': spacing['xl'],
    'padding-bottom': spacing['6xxl'],
  },
  none: {},
  'top-bottom': {
    'padding-left': spacing['none'],
    'padding-right': spacing['none'],
    'padding-top': spacing['xl'],
    'padding-bottom': spacing['6xxl'],
  },
};

const StyledAsideContainer = styled(AsideContainerComponent).withConfig({
  shouldForwardProp: prop => !['hideScrollbar', 'isInitialOpen', 'mode', 'variant'].includes(prop),
})<AsideContainerProps>`
  height: 100%;
  overflow-y: auto;
  flex-shrink: 0;
  position: relative;
  /* Make scrollbar overlay the content */

  & {
    scrollbar-width: thin; /* For modern browsers, make scrollbar thinner */
    scrollbar-color: #ccc transparent; /* Set thumb and track colors */
  }

  &::-webkit-scrollbar {
    width: 8px; /* Set scrollbar width */
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ccc; /* Scrollbar thumb color */
    border-radius: 4px; /* Rounded scrollbar */
  }

  &::-webkit-scrollbar-track {
    background-color: transparent; /* Transparent track to overlay content */
  }

  ${props => variants[props.variant || 'default']};
  ${props => modes[props.mode || 'default']}

  ${props => {
    return (
      props.hideScrollbar &&
      css`
        ::-webkit-scrollbar {
          width: 0; /* Remove scrollbar space */
          background: transparent;
        }
        scrollbar-width: none; /* Firefox 64 */
      `
    );
  }}
`;

export const AsideCollapsibleSection = styled(({ className, label, children }) => {
  const [open, setOpen] = React.useState(true);

  return (
    <Flex flexDirection={'column'} className={className}>
      <Flex
        componentClass={'button'}
        className={'label'}
        alignItems={'center'}
        paddingVertical={'xs'}
        onClick={() => setOpen(!open)}
        justifyContent={'space-between'}
        clickable
      >
        <Text variant={'gray800'} weight={500} marginBottom={'none'} marginRight={'xs'}>
          {label}
        </Text>
        <IconAnimatedChevron isOn={open} />
      </Flex>

      <Collapse isOpen={open}>{children}</Collapse>
    </Flex>
  );
})<{ className?: string; label: React.ReactNode | string; children?: React.ReactNode }>`
  .label {
    position: relative;
    border: none;
    background: white;
    transition: 0.2s;

    &:hover {
      :after {
        border-bottom: 1px solid ${colors.gray400};
      }
    }

    &:after {
      content: '';
      border-bottom: 1px solid ${colors.gray300};
      position: absolute;
      left: -40px;
      right: -8px;
      bottom: 0;
      width: calc(100% + 48px);
      transition: 0.2s;
    }
  }
`;

export const AsideSubSection = styled.section`
  margin-top: ${spacing.md};
  padding-bottom: ${spacing.sm};
  border-bottom: 1px solid ${colors.gray300};

  &:last-of-type {
    border-bottom: none;
  }
`;

const AsideContainer = (props: AsideContainerProps) => <StyledAsideContainer {...props} />;

AsideContainer.CollapsibleSection = AsideCollapsibleSection;
AsideContainer.SubSection = AsideSubSection;

export default AsideContainer;
